/**
 * State and colours for the reading ruler (see components/ReadingRuler.tsx): which cell is
 * pressed, and the shared tints. Escape clears it.
 */
import { useEffect, useRef, useState, useSyncExternalStore, type PointerEvent as ReactPointerEvent } from 'react';
import { modeColors } from '@/utils/modeColors';
import { loadPref, savePref } from './prefStorage';

export interface RulerCell {
  row: number;
  col: number;
}

/** Dim strength (alpha of dark slate): the pressed cell's row/column, and everything else. */
export const RULER_DIM_NEAR = 0.07;
export const RULER_DIM_FAR = 0.22;
/** Row-label tint / header tick: the same shade as the ARR/DEP pill. */
export const rulerColor = (mode?: string | null) => modeColors(mode).pill;

// Opt-in: the ruler only follows taps while it is switched on, and the choice is shared by
// both grids and remembered on this device. Off by default so taps never light up the grid
// unless the user asked for it.
const STORAGE_KEY = 'orca.readingRuler';
const listeners = new Set<() => void>();
let enabled = (() => {
  return loadPref(STORAGE_KEY) === '1';
})();

export function setRulerEnabled(on: boolean): void {
  enabled = on;
  savePref(STORAGE_KEY, on ? '1' : '0');
  listeners.forEach((l) => l());
}

export function useRulerEnabled(): boolean {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
    () => enabled,
  );
}

/** How far a press may wander and still count as a tap. */
const TAP_SLOP_PX = 8;

/** The ruler's state, plus the handler that moves it. Escape clears it. */
export function useReadingRuler() {
  const [stored, setRuler] = useState<RulerCell | null>(null);
  const on = useRulerEnabled();
  // Switching the ruler off clears it at once, wherever it was parked.
  const ruler = on ? stored : null;

  useEffect(() => {
    if (!ruler) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setRuler(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ruler]);

  /** Pressing the cell the ruler is already on clears it; anywhere else moves it. */
  const press = (cell: RulerCell | null) =>
    on && setRuler((prev) => (!cell || (prev && prev.row === cell.row && prev.col === cell.col) ? null : cell));

  // A tap is a pointerdown and pointerup in nearly the same place. Pointer events, not
  // click: the canvas cancels the browser's follow-up click on a touch tap (it calls
  // preventDefault on touchend), so a click listener never fires on a phone. A scroll
  // ends in pointercancel instead of pointerup, and a drag moves too far, so neither
  // moves the ruler.
  const down = useRef<{ x: number; y: number } | null>(null);
  const tapHandlers = (cellAt: (clientX: number, clientY: number) => RulerCell | null) => ({
    onPointerDownCapture: (e: ReactPointerEvent) => { down.current = { x: e.clientX, y: e.clientY }; },
    onPointerUpCapture: (e: ReactPointerEvent) => {
      const start = down.current;
      down.current = null;
      if (!start || Math.hypot(e.clientX - start.x, e.clientY - start.y) > TAP_SLOP_PX) return;
      if (on) press(cellAt(e.clientX, e.clientY));
    },
    onPointerCancelCapture: () => { down.current = null; },
  });

  return { ruler, press, tapHandlers, enabled: on, clear: () => setRuler(null) };
}
